import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
    formatDateTime,
    parseCustomDate,
    calculateActiveSLA,
    calculateTAT,
    calculateSLA,
    getClientSlaTargetHours
} from "../utils/helpers";

const formatWaitDuration = (dateTimeStr) => {
    if (!dateTimeStr || dateTimeStr === "-") return null;
    const parsed = parseCustomDate(dateTimeStr) || new Date(dateTimeStr);
    if (!parsed || isNaN(parsed.getTime())) return null;

    const diffMs = Date.now() - parsed.getTime();
    if (diffMs < 0) return "Just now";

    const totalMins = Math.floor(diffMs / 60000);
    if (totalMins < 1) return "Just now";
    if (totalMins < 60) return `${totalMins} mins`;

    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return mins > 0 ? `${hrs} hr ${mins} mins` : `${hrs} hr`;
};

const isClosedOrExceptionFile = (f) => {
    const statusVal = String(f.status || "").trim().toLowerCase();
    const substatus = String(f.substatus || "").trim().toLowerCase();

    return (
        statusVal === "completed" ||
        statusVal === "rejected" ||
        statusVal === "wrong report" ||
        statusVal === "do not review" ||
        statusVal === "complete by client" ||
        statusVal.includes("exception") ||
        substatus === "completed" ||
        substatus === "rejected" ||
        substatus === "wrong report" ||
        substatus === "do not review" ||
        substatus === "complete by client" ||
        substatus === "completed by client" ||
        substatus.includes("exception")
    );
};

const AccountManagerDashboard = ({
    currentUser = { name: "", role: "AccountManager" },
    clientTatMap = {},
    selectedClient = "All",
    shiftFilter = "All",
    selectedLocation = "All",
    onOpenWorkspace = null
}) => {
    const [allFiles, setAllFiles] = useState([]);
    const [registeredReviewers, setRegisteredReviewers] = useState([]);
    const [loading, setLoading] = useState(false);

    // Reviewer Availability Table Filters
    const [availabilityFilter, setAvailabilityFilter] = useState("All"); // "All" | "Available" | "Busy" | "HighLoad"
    const [expandedReviewerRows, setExpandedReviewerRows] = useState({});

    // Client-Wise Files Filters
    const [slaTab, setSlaTab] = useState("Active"); // "Active" | "Approaching" | "Breached" | "Within" | "Completed" | "All"
    const [typeFilter, setTypeFilter] = useState("All"); // "All" | "QC" | "Revised"
    const [dateRangeFilter, setDateRangeFilter] = useState("Today"); // "Today" | "Month" | "Custom"
    const [customStartDate, setCustomStartDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    });
    const [customEndDate, setCustomEndDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    });
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;
    const [reviewerFilter, setReviewerFilter] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [collapsedClients, setCollapsedClients] = useState({});

    const handleQuickRange = (rangeKey) => {
        const now = new Date();
        const yearStr = now.getFullYear();
        const monthStr = String(now.getMonth() + 1).padStart(2, "0");
        const dayStr = String(now.getDate()).padStart(2, "0");
        const todayStr = `${yearStr}-${monthStr}-${dayStr}`;

        if (rangeKey === "Today") {
            setCustomStartDate(todayStr);
            setCustomEndDate(todayStr);
            setDateRangeFilter("Today");
        } else if (rangeKey === "Month") {
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            setCustomStartDate(`${yearStr}-${monthStr}-01`);
            setCustomEndDate(`${yearStr}-${monthStr}-${String(lastDay).padStart(2, "0")}`);
            setDateRangeFilter("Month");
        }
    };

    const fetchDashboardData = useCallback(async (silent = false) => {
        const isSilent = silent === true;
        const token = localStorage.getItem("token");
        if (!token) return;

        if (!isSilent) setLoading(true);
        try {
            const now = new Date();
            const yearStr = now.getFullYear();
            const monthStr = String(now.getMonth() + 1).padStart(2, "0");
            const dayStr = String(now.getDate()).padStart(2, "0");
            const todayStr = `${yearStr}-${monthStr}-${dayStr}`;

            const rangeStart = customStartDate || todayStr;
            const rangeEnd = customEndDate || todayStr;

            const params = {
                department: "",
                client: selectedClient && selectedClient !== "All" && selectedClient !== "Dashboard" ? selectedClient : "All",
                shiftFilter: shiftFilter || "All",
                location: selectedLocation || "All",
                startDate: rangeStart,
                endDate: rangeEnd,
                includeActive: "true"
            };

            const [filesRes, usersRes] = await Promise.all([
                axios.get("/api/files/filter", {
                    params,
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get("/api/users/dropdown-list", {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: [] }))
            ]);

            if (Array.isArray(filesRes.data)) {
                setAllFiles(filesRes.data);
            } else {
                setAllFiles([]);
            }

            if (Array.isArray(usersRes.data)) {
                setRegisteredReviewers(usersRes.data);
            }
        } catch (err) {
            console.error("Error fetching Account Manager dashboard data:", err);
        } finally {
            if (!isSilent) setLoading(false);
        }
    }, [selectedClient, shiftFilter, selectedLocation, customStartDate, customEndDate]);

    useEffect(() => {
        fetchDashboardData(false);

        const baseUrl = axios.defaults.baseURL || "";
        const eventSource = new EventSource(`${baseUrl}/api/events`);
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === "FILES_UPDATED") {
                    fetchDashboardData(true);
                }
            } catch (err) {
                // Ignore parse errors
            }
        };

        return () => {
            eventSource.close();
        };
    }, [fetchDashboardData]);

    // Enrich files with 3-tier SLA visibility (Within SLA, Approaching SLA, Over SLA)
    const processedFiles = useMemo(() => {
        return (allFiles || [])
            .filter(f => {
                const assignedName = String(f.assigned_to || f.reviewer_name || "").trim();
                return assignedName !== "";
            })
            .map(f => {
                const assignedName = String(f.assigned_to || f.reviewer_name || "").trim();
                const rawDept = String(f.department || f.activeDept || "QC").trim();
                const isRevised = rawDept.toLowerCase() === "revised" || rawDept.toLowerCase() === "report writing";
                const fileType = isRevised ? "Revised" : "QC";

                const rawClient = String(f.client_name || f.activeClient || "Unknown Client").trim();
                const cleanClient = rawClient.toLowerCase() === "anm" ? "MMA" : rawClient;

                const isClosed = isClosedOrExceptionFile(f);
                const targetHours = getClientSlaTargetHours(
                    cleanClient,
                    fileType,
                    f.priority || "",
                    clientTatMap
                );
                const targetMins = Math.round(targetHours * 60);

                let tatDisplay = f.tat || "-";
                let slaCategory = "Within SLA"; // "Within SLA" | "Approaching SLA" | "Over SLA"
                let remainingMins = null;

                if (!isClosed && f.in_time && f.in_time !== "-") {
                    const liveSla = calculateActiveSLA(
                        f.in_time,
                        cleanClient,
                        fileType,
                        f.priority || "",
                        clientTatMap
                    );
                    tatDisplay = liveSla.tat;

                    const parsedIn = parseCustomDate(f.in_time);
                    if (parsedIn && !isNaN(parsedIn.getTime())) {
                        const elapsedMins = Math.floor((Date.now() - parsedIn.getTime()) / 60000);
                        remainingMins = targetMins - elapsedMins;
                        const warningThreshold = isRevised ? 15 : (targetMins <= 30 ? 15 : 30);

                        if (elapsedMins > targetMins) {
                            slaCategory = "Over SLA";
                        } else if (remainingMins <= warningThreshold) {
                            slaCategory = "Approaching SLA";
                        } else {
                            slaCategory = "Within SLA";
                        }
                    } else {
                        slaCategory = liveSla.status === "Over SLA" ? "Over SLA" : "Within SLA";
                    }
                } else if (isClosed && f.in_time && f.in_time !== "-") {
                    if (f.out_time && f.out_time !== "-") {
                        if (!tatDisplay || tatDisplay === "-") {
                            tatDisplay = calculateTAT(f.in_time, f.out_time) || "-";
                        }
                    }
                    const rawSla = f.sla_status && f.sla_status !== "-"
                        ? f.sla_status
                        : calculateSLA(tatDisplay, cleanClient, fileType, f.priority || "", clientTatMap);
                    const lowerSla = String(rawSla || "").toLowerCase();
                    if (lowerSla.includes("over") || lowerSla.includes("exceed")) {
                        slaCategory = "Over SLA";
                    } else {
                        slaCategory = "Within SLA";
                    }
                }

                const rawStatus = String(f.status || "").trim();
                const rawStatusLower = rawStatus.toLowerCase();
                const displayStatus = !isClosed && (!rawStatus || rawStatusLower === "new" || rawStatusLower.includes("not assign"))
                    ? "Assigned"
                    : (rawStatus || "Assigned");

                return {
                    ...f,
                    assignedName,
                    cleanClient,
                    fileType,
                    isClosed,
                    tatDisplay,
                    slaCategory,
                    remainingMins,
                    targetHours,
                    displayStatus,
                    assignedWaitText: !isClosed ? formatWaitDuration(f.assign_time || f.in_time) : null
                };
            });
    }, [allFiles, clientTatMap]);

    // Build Reviewer Availability & Workload Distribution list
    const reviewerWorkloadList = useMemo(() => {
        const map = {};

        // Seed with all registered reviewers
        (registeredReviewers || []).forEach(r => {
            const rName = String(typeof r === "string" ? r : r.name || "").trim();
            if (!rName) return;
            const key = rName.toLowerCase();
            if (!map[key]) {
                map[key] = {
                    name: rName,
                    assigned: 0,
                    working: 0,
                    activeTotal: 0,
                    qcActive: 0,
                    revisedActive: 0,
                    withinSla: 0,
                    approachingSla: 0,
                    breachedSla: 0,
                    completed: 0,
                    activeFiles: []
                };
            }
        });

        // Aggregate file counts per reviewer
        processedFiles.forEach(file => {
            const rName = file.assignedName;
            const key = rName.toLowerCase();
            if (!map[key]) {
                map[key] = {
                    name: rName,
                    assigned: 0,
                    working: 0,
                    activeTotal: 0,
                    qcActive: 0,
                    revisedActive: 0,
                    withinSla: 0,
                    approachingSla: 0,
                    breachedSla: 0,
                    completed: 0,
                    activeFiles: []
                };
            }

            const entry = map[key];
            if (file.isClosed) {
                entry.completed += 1;
            } else {
                const sLower = String(file.displayStatus || "").toLowerCase();
                if (sLower === "working" || sLower === "in progress") {
                    entry.working += 1;
                } else {
                    entry.assigned += 1;
                }
                entry.activeTotal += 1;

                if (file.fileType === "QC") entry.qcActive += 1;
                else entry.revisedActive += 1;

                if (file.slaCategory === "Over SLA") entry.breachedSla += 1;
                else if (file.slaCategory === "Approaching SLA") entry.approachingSla += 1;
                else entry.withinSla += 1;

                entry.activeFiles.push({
                    client_name: file.cleanClient,
                    file_type: file.fileType,
                    assign_time: file.assign_time || file.in_time,
                    wait_text: file.assignedWaitText || "Just now",
                    sla_category: file.slaCategory
                });
            }
        });

        return Object.values(map).sort((a, b) => {
            if (b.activeTotal !== a.activeTotal) return b.activeTotal - a.activeTotal;
            return a.name.localeCompare(b.name);
        });
    }, [registeredReviewers, processedFiles]);

    // Filter Reviewer Availability rows
    const filteredReviewers = useMemo(() => {
        return reviewerWorkloadList.filter(r => {
            if (availabilityFilter === "Available") return r.activeTotal === 0;
            if (availabilityFilter === "Busy") return r.activeTotal >= 1 && r.activeTotal <= 2;
            if (availabilityFilter === "HighLoad") return r.activeTotal >= 3;
            return true;
        });
    }, [reviewerWorkloadList, availabilityFilter]);

    // Top Summary Metrics
    const summaryMetrics = useMemo(() => {
        const activeFiles = processedFiles.filter(f => !f.isClosed);
        const completedFiles = processedFiles.filter(f => f.isClosed);

        const withinCount = activeFiles.filter(f => f.slaCategory === "Within SLA").length;
        const approachingCount = activeFiles.filter(f => f.slaCategory === "Approaching SLA").length;
        const breachedCount = activeFiles.filter(f => f.slaCategory === "Over SLA").length;

        const availableRevCount = reviewerWorkloadList.filter(r => r.activeTotal === 0).length;
        const busyRevCount = reviewerWorkloadList.filter(r => r.activeTotal > 0).length;

        return {
            activeTotal: activeFiles.length,
            completedTotal: completedFiles.length,
            allTotal: processedFiles.length,
            withinCount,
            approachingCount,
            breachedCount,
            availableRevCount,
            busyRevCount
        };
    }, [processedFiles, reviewerWorkloadList]);

    // Filter files for Client-Wise Categorized Section
    const filteredFiles = useMemo(() => {
        return processedFiles.filter(f => {
            if (slaTab === "Active" && f.isClosed) return false;
            if (slaTab === "Approaching" && (f.isClosed || f.slaCategory !== "Approaching SLA")) return false;
            if (slaTab === "Breached" && (f.isClosed || f.slaCategory !== "Over SLA")) return false;
            if (slaTab === "Within" && (f.isClosed || f.slaCategory !== "Within SLA")) return false;
            if (slaTab === "Completed" && !f.isClosed) return false;

            if (typeFilter !== "All" && f.fileType !== typeFilter) return false;

            if (reviewerFilter !== "All") {
                if (f.assignedName.toLowerCase() !== reviewerFilter.toLowerCase()) return false;
            }

            if (searchQuery.trim() !== "") {
                const q = searchQuery.trim().toLowerCase();
                const fileNum = String(f.file_number || f.loan_number || "").toLowerCase();
                const cli = String(f.cleanClient || "").toLowerCase();
                const rev = String(f.assignedName || "").toLowerCase();
                const addr = String(f.address || "").toLowerCase();
                if (!fileNum.includes(q) && !cli.includes(q) && !rev.includes(q) && !addr.includes(q)) {
                    return false;
                }
            }

            return true;
        });
    }, [processedFiles, slaTab, typeFilter, reviewerFilter, searchQuery]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [slaTab, typeFilter, dateRangeFilter, customStartDate, customEndDate, reviewerFilter, searchQuery]);

    const totalFilteredCount = filteredFiles.length;
    const totalPages = Math.max(1, Math.ceil(totalFilteredCount / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedFiles = useMemo(() => {
        const startIdx = (safePage - 1) * PAGE_SIZE;
        return filteredFiles.slice(startIdx, startIdx + PAGE_SIZE);
    }, [filteredFiles, safePage]);

    // Group paginated files by Client (max 50 rows rendered at a time)
    const clientGroups = useMemo(() => {
        const map = {};
        paginatedFiles.forEach(file => {
            const clientKey = file.cleanClient || "Other";
            if (!map[clientKey]) {
                map[clientKey] = {
                    clientName: clientKey,
                    files: [],
                    qcCount: 0,
                    revisedCount: 0,
                    approachingCount: 0,
                    breachedCount: 0
                };
            }
            map[clientKey].files.push(file);
            if (file.fileType === "QC") map[clientKey].qcCount += 1;
            else map[clientKey].revisedCount += 1;

            if (!file.isClosed) {
                if (file.slaCategory === "Over SLA") map[clientKey].breachedCount += 1;
                else if (file.slaCategory === "Approaching SLA") map[clientKey].approachingCount += 1;
            }
        });

        return Object.values(map).sort((a, b) => {
            if (b.breachedCount !== a.breachedCount) return b.breachedCount - a.breachedCount;
            if (b.approachingCount !== a.approachingCount) return b.approachingCount - a.approachingCount;
            if (b.files.length !== a.files.length) return b.files.length - a.files.length;
            return a.clientName.localeCompare(b.clientName);
        });
    }, [paginatedFiles]);

    const toggleReviewerExpand = (revName) => {
        setExpandedReviewerRows(prev => ({
            ...prev,
            [revName]: !prev[revName]
        }));
    };

    const toggleClientCollapse = (clientName) => {
        setCollapsedClients(prev => ({
            ...prev,
            [clientName]: !prev[clientName]
        }));
    };

    const getStatusBadgeStyle = (statusStr) => {
        const s = String(statusStr || "").trim().toLowerCase();
        if (s === "completed") return { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" };
        if (s === "rejected") return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
        if (s === "working" || s === "in progress") return { bg: "#fef3c7", text: "#b45309", border: "#fde68a" };
        if (s.includes("revision")) return { bg: "#f3e8ff", text: "#7c3aed", border: "#ddd6fe" };
        return { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" };
    };

    const getSlaBadgeStyle = (slaCategory) => {
        if (slaCategory === "Over SLA") return { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" };
        if (slaCategory === "Approaching SLA") return { bg: "#fef3c7", text: "#b45309", border: "#fcd34d" };
        return { bg: "#dcfce7", text: "#15803d", border: "#86efac" };
    };

    const getPriorityBadgeStyle = (priorityStr) => {
        const p = String(priorityStr || "").trim().toLowerCase();
        if (p === "vip") return { bg: "#fff1f2", text: "#e11d48", border: "#fecdd3" };
        if (p.includes("rush")) return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
        if (p.includes("late") || p.includes("past")) return { bg: "#ffedd5", text: "#c2410c", border: "#fed7aa" };
        if (p.includes("due")) return { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" };
        return { bg: "#f8fafc", text: "#475569", border: "#e2e8f0" };
    };

    return (
        <div style={{ padding: "24px 30px", width: "100%", boxSizing: "border-box" }}>
            {/* Top Header Banner */}
            <div style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "20px 24px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
                marginBottom: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px"
            }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ fontSize: "22px" }}>📊</span>
                        <h2 style={{ margin: 0, fontSize: "19px", fontWeight: "900", color: "#0f172a" }}>
                            Account Manager Dashboard — {currentUser.name}
                        </h2>
                    </div>
                    <p style={{ margin: "4px 0 0 32px", fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
                        Reviewer availability, workload distribution, client-wise assigned files & real-time SLA monitoring
                    </p>
                </div>
            </div>

            {/* Upper KPI Summary Cards (Matching Admin Dashboard Card CSS) */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                gap: "10px",
                marginBottom: "24px"
            }}>
                {[
                    { label: "Active Assigned", count: summaryMetrics.activeTotal, color: "#3182ce", bg: "#ebf8ff" },
                    { label: "Available Reviewers", count: summaryMetrics.availableRevCount, color: "#38a169", bg: "#f0fff4" },
                    { label: "Busy Reviewers", count: summaryMetrics.busyRevCount, color: "#00a3e0", bg: "#e6f7ff" },
                    { label: "Within SLA", count: summaryMetrics.withinCount, color: "#2f855a", bg: "#f0fff4" },
                    { label: "Approaching SLA", count: summaryMetrics.approachingCount, color: "#d69e2e", bg: "#fffff0" },
                    { label: "Breached SLA", count: summaryMetrics.breachedCount, color: "#e53e3e", bg: "#fff5f5" },
                    { label: "Completed", count: summaryMetrics.completedTotal, color: "#7c3aed", bg: "#f5f3ff" }
                ].map((card, idx) => (
                    <div
                        key={idx}
                        style={{
                            backgroundColor: card.bg,
                            borderRadius: "10px",
                            padding: "15px",
                            border: `1px solid ${card.color}20`,
                            textAlign: "center"
                        }}
                    >
                        <div style={{ fontSize: "20px", fontWeight: "900", color: card.color }}>{card.count}</div>
                        <div style={{ fontSize: "10px", color: "#666", marginTop: "5px" }}>{card.label}</div>
                    </div>
                ))}
            </div>

            {/* SECTION 1: REVIEWER AVAILABILITY & WORKLOAD DISTRIBUTION */}
            <div style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "20px 24px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
                marginBottom: "24px"
            }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                    marginBottom: "16px"
                }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "900", color: "#0f172a" }}>
                            👥 Reviewer Availability & Workload Distribution
                        </h3>
                        <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>
                            Click the <b>+</b> button on Assigned to view client names & waiting time, or filter files by reviewer
                        </span>
                    </div>

                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {[
                            { key: "All", label: `All Reviewers (${reviewerWorkloadList.length})` },
                            { key: "Available", label: `🟢 Available (${summaryMetrics.availableRevCount})` },
                            { key: "Busy", label: "🟡 Active Load (1–2)" },
                            { key: "HighLoad", label: "🔴 Heavy Load (3+)" }
                        ].map(btn => {
                            const isActive = availabilityFilter === btn.key;
                            return (
                                <button
                                    key={btn.key}
                                    type="button"
                                    onClick={() => setAvailabilityFilter(btn.key)}
                                    style={{
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: "800",
                                        cursor: "pointer",
                                        border: isActive ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
                                        backgroundColor: isActive ? "#2563eb" : "#f8fafc",
                                        color: isActive ? "white" : "#475569"
                                    }}
                                >
                                    {btn.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div style={{ maxHeight: "360px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed", minWidth: "900px" }}>
                        <thead>
                            <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0", position: "sticky", top: 0, zIndex: 5 }}>
                                <th style={{ width: "18%", padding: "11px 16px", textAlign: "left", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>Reviewer Name</th>
                                <th style={{ width: "14%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>Availability</th>
                                <th style={{ width: "12%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>Assigned</th>
                                <th style={{ width: "10%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>Working</th>
                                <th style={{ width: "14%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>QC / Revised</th>
                                <th style={{ width: "18%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>SLA Health (Active)</th>
                                <th style={{ width: "7%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>Done</th>
                                <th style={{ width: "7%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase" }}>Filter</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReviewers.map((rev, idx) => {
                                const isExpanded = !!expandedReviewerRows[rev.name];
                                const hasActive = rev.activeTotal >= 1;
                                const isSelectedFilter = reviewerFilter.toLowerCase() === rev.name.toLowerCase();

                                let availBadge = { label: "🟢 Available", bg: "#dcfce7", text: "#15803d", border: "#86efac" };
                                if (rev.activeTotal >= 3) {
                                    availBadge = { label: `🔴 High Load (${rev.activeTotal})`, bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" };
                                } else if (rev.activeTotal >= 1) {
                                    availBadge = { label: `🟡 Busy (${rev.activeTotal})`, bg: "#fef3c7", text: "#b45309", border: "#fcd34d" };
                                }

                                return (
                                    <React.Fragment key={rev.name || idx}>
                                        <tr style={{
                                            borderBottom: isExpanded && hasActive ? "none" : "1px solid #f1f5f9",
                                            backgroundColor: isSelectedFilter ? "#eff6ff" : "white"
                                        }}>
                                            <td style={{ padding: "12px 16px", fontWeight: "700", color: "#1e293b" }}>{rev.name}</td>
                                            <td style={{ padding: "12px 12px", textAlign: "center" }}>
                                                <span style={{
                                                    display: "inline-block",
                                                    padding: "3px 10px",
                                                    borderRadius: "999px",
                                                    fontSize: "11px",
                                                    fontWeight: "800",
                                                    backgroundColor: availBadge.bg,
                                                    color: availBadge.text,
                                                    border: `1px solid ${availBadge.border}`
                                                }}>
                                                    {availBadge.label}
                                                </span>
                                            </td>
                                            <td style={{ padding: "12px 12px", textAlign: "center" }}>
                                                {hasActive ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleReviewerExpand(rev.name)}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            gap: "5px",
                                                            backgroundColor: isExpanded ? "#2563eb" : "#eff6ff",
                                                            color: isExpanded ? "#ffffff" : "#1d4ed8",
                                                            border: isExpanded ? "1px solid #1d4ed8" : "1px solid #bfdbfe",
                                                            borderRadius: "6px",
                                                            padding: "3px 8px",
                                                            cursor: "pointer",
                                                            fontWeight: "900",
                                                            fontSize: "13px"
                                                        }}
                                                    >
                                                        <span>{rev.activeTotal}</span>
                                                        <span style={{ fontSize: "11px", fontWeight: "900" }}>{isExpanded ? "−" : "+"}</span>
                                                    </button>
                                                ) : (
                                                    <span style={{ color: "#94a3b8", fontWeight: "800" }}>0</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "12px 12px", textAlign: "center", fontWeight: "800", color: rev.working > 0 ? "#d97706" : "#94a3b8" }}>
                                                {rev.working}
                                            </td>
                                            <td style={{ padding: "12px 12px", textAlign: "center" }}>
                                                {hasActive ? (
                                                    <div style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
                                                        <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", padding: "2px 7px", borderRadius: "4px", fontSize: "11px", fontWeight: "800" }}>
                                                            QC: {rev.qcActive}
                                                        </span>
                                                        <span style={{ backgroundColor: "#f3e8ff", color: "#7c3aed", padding: "2px 7px", borderRadius: "4px", fontSize: "11px", fontWeight: "800" }}>
                                                            Rev: {rev.revisedActive}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: "#cbd5e1" }}>-</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "12px 12px", textAlign: "center" }}>
                                                {hasActive ? (
                                                    <div style={{ display: "inline-flex", gap: "5px", alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                                                        {rev.withinSla > 0 && (
                                                            <span style={{ backgroundColor: "#dcfce7", color: "#15803d", padding: "2px 7px", borderRadius: "4px", fontSize: "11px", fontWeight: "800" }}>
                                                                {rev.withinSla} OK
                                                            </span>
                                                        )}
                                                        {rev.approachingSla > 0 && (
                                                            <span style={{ backgroundColor: "#fef3c7", color: "#b45309", padding: "2px 7px", borderRadius: "4px", fontSize: "11px", fontWeight: "800" }}>
                                                                ⚠️ {rev.approachingSla} Risk
                                                            </span>
                                                        )}
                                                        {rev.breachedSla > 0 && (
                                                            <span style={{ backgroundColor: "#fee2e2", color: "#b91c1c", padding: "2px 7px", borderRadius: "4px", fontSize: "11px", fontWeight: "800" }}>
                                                                🔴 {rev.breachedSla} Over
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: "#cbd5e1" }}>-</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "12px 12px", textAlign: "center", fontWeight: "900", color: "#15803d" }}>
                                                {rev.completed}
                                            </td>
                                            <td style={{ padding: "12px 12px", textAlign: "center" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setReviewerFilter(isSelectedFilter ? "All" : rev.name)}
                                                    style={{
                                                        padding: "4px 8px",
                                                        borderRadius: "6px",
                                                        fontSize: "11px",
                                                        fontWeight: "800",
                                                        cursor: "pointer",
                                                        border: isSelectedFilter ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
                                                        backgroundColor: isSelectedFilter ? "#2563eb" : "#f8fafc",
                                                        color: isSelectedFilter ? "white" : "#334155"
                                                    }}
                                                >
                                                    {isSelectedFilter ? "Showing" : "View"}
                                                </button>
                                            </td>
                                        </tr>

                                        {isExpanded && hasActive && (
                                            <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                                <td colSpan={8} style={{ padding: "8px 16px 12px 16px" }}>
                                                    <div style={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: "6px",
                                                        padding: "8px 14px"
                                                    }}>
                                                        <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>
                                                            Files ({rev.activeFiles.length}):
                                                        </span>
                                                        {rev.activeFiles.map((af, fIdx) => (
                                                            <span
                                                                key={fIdx}
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: "6px",
                                                                    backgroundColor: "#f8fafc",
                                                                    border: "1px solid #cbd5e1",
                                                                    borderRadius: "6px",
                                                                    padding: "4px 10px",
                                                                    fontSize: "12px"
                                                                }}
                                                            >
                                                                <span style={{ fontWeight: "800", color: "#1e293b" }}>{af.client_name}</span>
                                                                <span style={{
                                                                    fontSize: "10px",
                                                                    fontWeight: "800",
                                                                    padding: "1px 5px",
                                                                    borderRadius: "4px",
                                                                    backgroundColor: af.file_type === "Revised" ? "#f3e8ff" : "#e0f2fe",
                                                                    color: af.file_type === "Revised" ? "#7c3aed" : "#0369a1"
                                                                }}>
                                                                    {af.file_type}
                                                                </span>
                                                                <span style={{
                                                                    fontWeight: "800",
                                                                    color: af.sla_category === "Over SLA" ? "#b91c1c" : (af.sla_category === "Approaching SLA" ? "#b45309" : "#1d4ed8"),
                                                                    backgroundColor: af.sla_category === "Over SLA" ? "#fee2e2" : (af.sla_category === "Approaching SLA" ? "#fef3c7" : "#eff6ff"),
                                                                    borderRadius: "4px",
                                                                    padding: "1px 6px",
                                                                    fontSize: "11px"
                                                                }}>
                                                                    ⏱️ {af.wait_text}
                                                                </span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* SECTION 2: CLIENT-WISE ASSIGNED FILES & SLA MONITOR */}
            <div style={{
                backgroundColor: "white",
                borderRadius: "10px",
                padding: "14px 20px",
                border: "1px solid #e2e8f0",
                marginBottom: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px"
            }}>
                {/* SLA & Status Filter Tabs */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    {[
                        { key: "Active", label: `Active Assigned (${summaryMetrics.activeTotal})` },
                        { key: "Approaching", label: `⚠️ Approaching SLA (${summaryMetrics.approachingCount})` },
                        { key: "Breached", label: `🔴 Breached SLA (${summaryMetrics.breachedCount})` },
                        { key: "Within", label: `🟢 Within SLA (${summaryMetrics.withinCount})` },
                        { key: "Completed", label: `Completed (${summaryMetrics.completedTotal})` },
                        { key: "All", label: `All Files (${summaryMetrics.allTotal})` }
                    ].map(tab => {
                        const isActive = slaTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setSlaTab(tab.key)}
                                style={{
                                    padding: "7px 12px",
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                    fontWeight: "800",
                                    cursor: "pointer",
                                    border: isActive ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
                                    backgroundColor: isActive ? "#2563eb" : "#f8fafc",
                                    color: isActive ? "white" : "#475569"
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}

                    <div style={{ width: "1px", height: "22px", backgroundColor: "#e2e8f0", margin: "0 4px" }} />

                    {/* File Type Pills */}
                    {["All", "QC", "Revised"].map(type => {
                        const isActive = typeFilter === type;
                        const activeColor = type === "QC" ? "#0284c7" : (type === "Revised" ? "#7c3aed" : "#0f172a");
                        return (
                            <button
                                key={type}
                                type="button"
                                onClick={() => setTypeFilter(type)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "800",
                                    cursor: "pointer",
                                    border: isActive ? `1px solid ${activeColor}` : "1px solid #e2e8f0",
                                    backgroundColor: isActive ? activeColor : "white",
                                    color: isActive ? "white" : "#64748b"
                                }}
                            >
                                {type === "All" ? "All Types" : `${type} Only`}
                            </button>
                        );
                    })}

                    <div style={{ width: "1px", height: "22px", backgroundColor: "#e2e8f0", margin: "0 4px" }} />

                    {/* Date Range Pills (Today / This Month) + Custom From/To Date Pickers */}
                    {[
                        { key: "Today", label: "Today" },
                        { key: "Month", label: "This Month" }
                    ].map(range => {
                        const isActive = dateRangeFilter === range.key;
                        return (
                            <button
                                key={range.key}
                                type="button"
                                onClick={() => handleQuickRange(range.key)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: "800",
                                    cursor: "pointer",
                                    border: isActive ? "1px solid #00a3e0" : "1px solid #cbd5e1",
                                    backgroundColor: isActive ? "#00a3e0" : "white",
                                    color: isActive ? "white" : "#475569"
                                }}
                            >
                                {range.label}
                            </button>
                        );
                    })}

                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>From:</label>
                        <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => {
                                setCustomStartDate(e.target.value);
                                setDateRangeFilter("Custom");
                            }}
                            style={{
                                padding: "5px 8px",
                                borderRadius: "6px",
                                border: dateRangeFilter === "Custom" ? "1px solid #00a3e0" : "1px solid #cbd5e1",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#0f172a",
                                backgroundColor: "white",
                                outline: "none"
                            }}
                        />
                        <label style={{ fontSize: "11px", fontWeight: "800", color: "#64748b" }}>To:</label>
                        <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => {
                                setCustomEndDate(e.target.value);
                                setDateRangeFilter("Custom");
                            }}
                            style={{
                                padding: "5px 8px",
                                borderRadius: "6px",
                                border: dateRangeFilter === "Custom" ? "1px solid #00a3e0" : "1px solid #cbd5e1",
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#0f172a",
                                backgroundColor: "white",
                                outline: "none"
                            }}
                        />
                    </div>
                </div>

                {/* Reviewer Select & Search */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <select
                        value={reviewerFilter}
                        onChange={(e) => setReviewerFilter(e.target.value)}
                        style={{
                            padding: "7px 12px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "#0f172a",
                            backgroundColor: "#f8fafc",
                            cursor: "pointer"
                        }}
                    >
                        <option value="All">All Reviewers</option>
                        {reviewerWorkloadList.map((r, idx) => (
                            <option key={idx} value={r.name}>{r.name} ({r.activeTotal} active)</option>
                        ))}
                    </select>

                    <input
                        type="text"
                        placeholder="Search File #, Client, Reviewer..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            padding: "7px 14px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            fontSize: "12px",
                            minWidth: "220px",
                            outline: "none"
                        }}
                    />
                </div>
            </div>

            {/* Client-Wise Categorized Cards */}
            {loading ? (
                <div style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "48px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0",
                    color: "#64748b",
                    fontWeight: "700"
                }}>
                    Loading client-wise assigned files...
                </div>
            ) : clientGroups.length === 0 ? (
                <div style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "48px",
                    textAlign: "center",
                    border: "1px solid #e2e8f0"
                }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>✅</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#1e293b" }}>
                        No files matching the selected filter
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                        Try switching the SLA filter tabs or clearing the reviewer filter.
                    </div>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                    {clientGroups.map(group => {
                        const isCollapsed = !!collapsedClients[group.clientName];
                        return (
                            <div
                                key={group.clientName}
                                style={{
                                    backgroundColor: "white",
                                    borderRadius: "12px",
                                    border: group.breachedCount > 0 ? "1px solid #fecaca" : (group.approachingCount > 0 ? "1px solid #fde68a" : "1px solid #e2e8f0"),
                                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
                                    overflow: "hidden"
                                }}
                            >
                                {/* Client Card Header */}
                                <div
                                    onClick={() => toggleClientCollapse(group.clientName)}
                                    style={{
                                        padding: "14px 20px",
                                        backgroundColor: group.breachedCount > 0 ? "#fff5f5" : (group.approachingCount > 0 ? "#fffbeb" : "#f8fafc"),
                                        borderBottom: isCollapsed ? "none" : "1px solid #e2e8f0",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        flexWrap: "wrap",
                                        gap: "10px"
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <span style={{ fontSize: "13px", fontWeight: "900", color: "#475569", width: "16px" }}>
                                            {isCollapsed ? "▶" : "▼"}
                                        </span>
                                        <span style={{ fontSize: "16px" }}>🏢</span>
                                        <span style={{ fontSize: "15px", fontWeight: "900", color: "#0f172a" }}>
                                            {group.clientName}
                                        </span>
                                        <span style={{
                                            backgroundColor: "#e2e8f0",
                                            color: "#1e293b",
                                            borderRadius: "999px",
                                            padding: "2px 10px",
                                            fontSize: "12px",
                                            fontWeight: "800"
                                        }}>
                                            {group.files.length} {group.files.length === 1 ? "File" : "Files"}
                                        </span>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                        {group.qcCount > 0 && (
                                            <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", borderRadius: "6px", padding: "3px 10px", fontSize: "11px", fontWeight: "800" }}>
                                                QC: {group.qcCount}
                                            </span>
                                        )}
                                        {group.revisedCount > 0 && (
                                            <span style={{ backgroundColor: "#f3e8ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: "6px", padding: "3px 10px", fontSize: "11px", fontWeight: "800" }}>
                                                Revised: {group.revisedCount}
                                            </span>
                                        )}
                                        {group.approachingCount > 0 && (
                                            <span style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fcd34d", borderRadius: "6px", padding: "3px 10px", fontSize: "11px", fontWeight: "800" }}>
                                                ⚠️ {group.approachingCount} Approaching SLA
                                            </span>
                                        )}
                                        {group.breachedCount > 0 && (
                                            <span style={{ backgroundColor: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: "6px", padding: "3px 10px", fontSize: "11px", fontWeight: "800" }}>
                                                🔴 {group.breachedCount} Breached SLA
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Client Files Table */}
                                {!isCollapsed && (
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed", minWidth: "960px" }}>
                                            <thead>
                                                <tr style={{ backgroundColor: "#ffffff", borderBottom: "2px solid #f1f5f9" }}>
                                                    <th style={{ width: "15%", padding: "11px 16px", textAlign: "left", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>File Number</th>
                                                    <th style={{ width: "13%", padding: "11px 12px", textAlign: "left", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Assigned Reviewer</th>
                                                    <th style={{ width: "8%", padding: "11px 10px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Type</th>
                                                    <th style={{ width: "9%", padding: "11px 10px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Priority</th>
                                                    <th style={{ width: "13%", padding: "11px 10px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>File In-Time</th>
                                                    <th style={{ width: "14%", padding: "11px 10px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Assign Time</th>
                                                    <th style={{ width: "10%", padding: "11px 10px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Status</th>
                                                    <th style={{ width: "18%", padding: "11px 12px", textAlign: "center", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>SLA Visibility</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.files.map((file, idx) => {
                                                    const statusStyle = getStatusBadgeStyle(file.displayStatus);
                                                    const slaStyle = getSlaBadgeStyle(file.slaCategory);
                                                    const prioStyle = getPriorityBadgeStyle(file.priority);

                                                    return (
                                                        <tr
                                                            key={file.id || idx}
                                                            style={{
                                                                borderBottom: "1px solid #f1f5f9",
                                                                backgroundColor: !file.isClosed && file.slaCategory === "Over SLA"
                                                                    ? "#fffafa"
                                                                    : (!file.isClosed && file.slaCategory === "Approaching SLA" ? "#fffef5" : "white")
                                                            }}
                                                        >
                                                            <td style={{ padding: "12px 16px", fontWeight: "800", color: "#0f172a" }}>
                                                                <div>{file.file_number || file.loan_number || "-"}</div>
                                                                {file.address && file.address !== "-" && (
                                                                    <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "500", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file.address}>
                                                                        {file.address}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            <td style={{ padding: "12px 12px", fontWeight: "700", color: "#1e293b" }}>
                                                                {file.assignedName}
                                                            </td>

                                                            <td style={{ padding: "12px 10px", textAlign: "center" }}>
                                                                <span style={{
                                                                    display: "inline-block",
                                                                    padding: "4px 10px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "900",
                                                                    backgroundColor: file.fileType === "Revised" ? "#f3e8ff" : "#e0f2fe",
                                                                    color: file.fileType === "Revised" ? "#7c3aed" : "#0369a1",
                                                                    border: file.fileType === "Revised" ? "1px solid #ddd6fe" : "1px solid #bae6fd"
                                                                }}>
                                                                    {file.fileType}
                                                                </span>
                                                            </td>

                                                            <td style={{ padding: "12px 10px", textAlign: "center" }}>
                                                                <span style={{
                                                                    display: "inline-block",
                                                                    padding: "3px 9px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "800",
                                                                    backgroundColor: prioStyle.bg,
                                                                    color: prioStyle.text,
                                                                    border: `1px solid ${prioStyle.border}`
                                                                }}>
                                                                    {file.priority || "Standard"}
                                                                </span>
                                                            </td>

                                                            <td style={{ padding: "12px 10px", textAlign: "center", fontWeight: "600", color: "#334155", whiteSpace: "nowrap" }}>
                                                                {formatDateTime(file.in_time) || file.in_time || "-"}
                                                            </td>

                                                            <td style={{ padding: "12px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
                                                                <div style={{ fontWeight: "600", color: "#334155" }}>
                                                                    {formatDateTime(file.assign_time) || file.assign_time || "-"}
                                                                </div>
                                                                {file.assignedWaitText && (
                                                                    <div style={{ fontSize: "11px", fontWeight: "800", color: "#2563eb", marginTop: "2px" }}>
                                                                        ⏱️ {file.assignedWaitText} ago
                                                                    </div>
                                                                )}
                                                            </td>

                                                            <td style={{ padding: "12px 10px", textAlign: "center" }}>
                                                                <span style={{
                                                                    display: "inline-block",
                                                                    padding: "4px 10px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: "800",
                                                                    backgroundColor: statusStyle.bg,
                                                                    color: statusStyle.text,
                                                                    border: `1px solid ${statusStyle.border}`
                                                                }}>
                                                                    {file.displayStatus}
                                                                </span>
                                                            </td>

                                                            <td style={{ padding: "12px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                                    <span style={{
                                                                        padding: "4px 10px",
                                                                        borderRadius: "6px",
                                                                        fontSize: "11px",
                                                                        fontWeight: "900",
                                                                        backgroundColor: slaStyle.bg,
                                                                        color: slaStyle.text,
                                                                        border: `1px solid ${slaStyle.border}`
                                                                    }}>
                                                                        {file.slaCategory}
                                                                    </span>
                                                                    <span style={{ fontSize: "12px", fontWeight: "800", color: "#1e293b" }}>
                                                                        ({file.tatDisplay || "-"})
                                                                    </span>
                                                                </div>
                                                                <div style={{ fontSize: "10px", color: "#64748b", marginTop: "3px", fontWeight: "600" }}>
                                                                    Target: {file.targetHours}h
                                                                    {!file.isClosed && file.remainingMins !== null && file.remainingMins > 0 && (
                                                                        <span style={{ color: file.slaCategory === "Approaching SLA" ? "#b45309" : "#15803d", fontWeight: "800", marginLeft: "6px" }}>
                                                                            • {file.remainingMins}m left
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Pagination Bar (50 rows per page) */}
                    {totalFilteredCount > PAGE_SIZE && (
                        <div style={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            padding: "12px 20px",
                            border: "1px solid #e2e8f0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "12px"
                        }}>
                            <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>
                                Showing <b>{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalFilteredCount)}</b> of <b>{totalFilteredCount}</b> files ({PAGE_SIZE} per page)
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <button
                                    type="button"
                                    disabled={safePage <= 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    style={{
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #cbd5e1",
                                        backgroundColor: safePage <= 1 ? "#f1f5f9" : "white",
                                        color: safePage <= 1 ? "#94a3b8" : "#0f172a",
                                        fontSize: "12px",
                                        fontWeight: "800",
                                        cursor: safePage <= 1 ? "not-allowed" : "pointer"
                                    }}
                                >
                                    ← Prev
                                </button>
                                <span style={{ fontSize: "12px", fontWeight: "800", color: "#0f172a", padding: "0 8px" }}>
                                    Page {safePage} of {totalPages}
                                </span>
                                <button
                                    type="button"
                                    disabled={safePage >= totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    style={{
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #cbd5e1",
                                        backgroundColor: safePage >= totalPages ? "#f1f5f9" : "white",
                                        color: safePage >= totalPages ? "#94a3b8" : "#0f172a",
                                        fontSize: "12px",
                                        fontWeight: "800",
                                        cursor: safePage >= totalPages ? "not-allowed" : "pointer"
                                    }}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AccountManagerDashboard;
